import { useEffect, useState, useSyncExternalStore } from 'react';
import { Menu, TextInput, Tooltip, UnstyledButton } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { NavLink } from 'react-router-dom';
import { can, roleTitle } from '@parts/shared';
import { errorText, putJson } from '../../api';
import { logout, useAuth } from '../../auth';
import { useI18n } from '../../i18n';
import {
  IconCart,
  IconChart,
  IconChevronDown,
  IconChevronsLeft,
  IconChevronsRight,
  IconFilePlus,
  IconHistory,
  IconLayers,
  IconLogout,
  IconPackage,
  IconPen,
  IconPulse,
  IconUndo,
  IconUser,
  IconUsers
} from '../../icons';
import { getTitle, setSidebarCollapsed, setTitle, subscribeSettings } from '../../settings';
import { useAppState } from '../../state';
import { openProfileDialog } from '../ProfileDialog';
import { Avatar } from '../ui';
import { openUndoDialog } from '../UndoLastAction';
import classes from './layout.module.css';

const NAV = [
  { title: 'Новый запрос', to: '/request', icon: IconFilePlus },
  { title: 'Закупка', to: '/purchase', icon: IconCart },
  { title: 'Текущие запросы', to: '/requests', icon: IconLayers, counter: true },
  { title: 'Статистика', to: '/stats', icon: IconChart },
  { title: 'История', to: '/history', icon: IconHistory }
];

/**
 * Боковая панель: название, разделы, пользователи (администратор), проверка сервера и меню пользователя.
 * Сворачивается до узкой полосы с иконками (положение запоминается для пользователя).
 */
export function Sidebar({ onNavigate, collapsed = false }: { onNavigate?: () => void; collapsed?: boolean }) {
  const { t, tx } = useI18n();
  const auth = useAuth();
  const user = auth.user!;
  const role = user.role;
  const { state } = useAppState();
  const title = useSyncExternalStore(subscribeSettings, getTitle);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(title);

  useEffect(() => setDraft(title), [title]);

  const reqs = state?.snapshot.reqs ?? [];
  const active = reqs.filter((r) => r.status !== 'Выполнен' && r.status !== 'Отменено').length;
  const overdue = reqs.filter((r) => r.status === 'Просрочен').length;

  const saveTitle = async () => {
    const value = draft.trim();
    setEditing(false);
    if (!value || value === title) {
      setDraft(title);
      return;
    }
    try {
      await putJson('/api/settings/title', { title: value });
      setTitle(value);
      document.title = value;
    } catch (err) {
      setDraft(title);
      notifications.show({ color: 'red', title: t('Не удалось сохранить название'), message: tx(errorText(err)) });
    }
  };

  const link = (to: string, label: string, Icon: typeof IconPulse, extra?: React.ReactNode, external = false) => (
    <Tooltip key={to} label={label} position="right" disabled={!collapsed}>
      {external ? (
        <a className={classes.navItem} href={to}>
          <Icon size={17} className={classes.navIcon} />
          <span className={classes.navLabel}>{label}</span>
          {extra}
        </a>
      ) : (
        <NavLink to={to} className={classes.navItem} onClick={onNavigate}>
          <Icon size={17} className={classes.navIcon} />
          <span className={classes.navLabel}>{label}</span>
          {extra}
        </NavLink>
      )}
    </Tooltip>
  );

  return (
    <div className={classes.side} data-collapsed={collapsed || undefined}>
      <div className={classes.brand}>
        <div className={classes.logo} aria-hidden>
          <IconPackage size={18} stroke={2} />
        </div>
        <div className={classes.brandText}>
          {editing ? (
            <TextInput
              value={draft}
              autoFocus
              size="xs"
              onChange={(e) => setDraft(e.target.value)}
              onBlur={() => void saveTitle()}
              onKeyDown={(e) => {
                if (e.key === 'Enter') void saveTitle();
                if (e.key === 'Escape') {
                  setDraft(title);
                  setEditing(false);
                }
              }}
            />
          ) : (
            <div
              className={classes.brandTitle}
              title={role === 'admin' ? t('Двойной щелчок — переименовать') : undefined}
              onDoubleClick={() => role === 'admin' && setEditing(true)}
            >
              {title || t('Учёт запчастей')}
            </div>
          )}
          <div className={classes.brandSub}>{t('Запросы и закупки')}</div>
        </div>
        {!onNavigate && (
          <Tooltip label={collapsed ? t('Развернуть панель') : t('Свернуть панель')} position="right">
            <UnstyledButton
              className={classes.collapseBtn}
              onClick={() => setSidebarCollapsed(!collapsed)}
              aria-label={collapsed ? t('Развернуть панель') : t('Свернуть панель')}
            >
              {collapsed ? <IconChevronsRight size={16} /> : <IconChevronsLeft size={16} />}
            </UnstyledButton>
          </Tooltip>
        )}
      </div>

      <nav className={classes.nav}>
        {NAV.map((item) =>
          link(
            item.to,
            t(item.title),
            item.icon,
            item.counter && active > 0 ? (
              <Tooltip label={overdue ? t('В работе: {n}, просрочено: {m}', { n: active, m: overdue }) : t('В работе: {n}', { n: active })}>
                <span className={classes.navCount} data-alert={overdue > 0 || undefined}>
                  {active}
                </span>
              </Tooltip>
            ) : null
          )
        )}
      </nav>

      <div className={classes.sideSection}>{t('Сервис')}</div>
      <nav className={classes.nav}>
        {can(role, 'USERS') &&
          link(
            '/users',
            t('Пользователи'),
            IconUsers,
            auth.pendingUsers > 0 ? (
              <Tooltip label={t('Ждут подтверждения: {n}', { n: auth.pendingUsers })}>
                <span className={classes.navCount} data-alert>
                  {auth.pendingUsers}
                </span>
              </Tooltip>
            ) : null
          )}
        {link('/status', t('Проверка сервера'), IconPulse, null, true)}
      </nav>

      <div className={classes.sideFoot}>
        <Menu position="top-start" width={240} offset={6}>
          <Menu.Target>
            <UnstyledButton className={classes.account} title={collapsed ? user.name : undefined}>
              <Avatar name={user.name} size={30} />
              <span className={classes.accountText}>
                <span className={classes.accountName}>{user.name}</span>
                <span className={classes.accountHint}>{tx(roleTitle(role))}</span>
              </span>
              <IconChevronDown size={15} className={classes.accountCaret} />
            </UnstyledButton>
          </Menu.Target>
          <Menu.Dropdown>
            <Menu.Label>{user.login}</Menu.Label>
            <Menu.Item leftSection={<IconUser size={14} />} onClick={openProfileDialog}>
              {t('Имя и пароль')}
            </Menu.Item>
            {role === 'admin' && (
              <>
                <Menu.Divider />
                <Menu.Item leftSection={<IconUndo size={14} />} onClick={openUndoDialog}>
                  {t('Отменить последнее действие')}
                </Menu.Item>
                <Menu.Item leftSection={<IconPen size={14} />} onClick={() => setEditing(true)}>
                  {t('Переименовать приложение')}
                </Menu.Item>
              </>
            )}
            <Menu.Divider />
            <Menu.Item leftSection={<IconLogout size={14} />} color="red" onClick={() => void logout()}>
              {t('Выйти')}
            </Menu.Item>
          </Menu.Dropdown>
        </Menu>
      </div>
    </div>
  );
}
